/**
 * Authentication Store for Admin Portal
 * Uses Zustand for state management similar to interface project
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { TokenManager } from '../lib/api/api-client';

// Types
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'ADMIN' | 'OPERATOR' | 'USER';
  emailVerified: boolean;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
  tokenType: string;
  expiresIn: number;
}

interface AuthState {
  // State
  user: User | null;
  authTokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setAuthTokens: (tokens: AuthTokens) => void;
  setUser: (user: User | null) => void;
  setAuthData: (tokens: AuthTokens, user: User) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  logout: () => void;
  reset: () => void;
  initialize: (tokens: AuthTokens | null, user: User | null) => void;
}

const initialState = {
  user: null,
  authTokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      ...initialState,

      setAuthTokens: (authTokens: AuthTokens) => {
        // Store tokens in localStorage
        TokenManager.setAuthSession(authTokens);
        
        set({
          authTokens,
          isAuthenticated: !!authTokens.accessToken,
          error: null,
        });
      },

      setUser: (user: User | null) => {
        if (user) {
          TokenManager.setUserData(user);
        }
        set({ user });
      },

      // Combined method to set both tokens and user atomically (prevents race conditions)
      setAuthData: (authTokens: AuthTokens, user: User) => {
        // Store both in localStorage
        TokenManager.setAuthSession(authTokens);
        TokenManager.setUserData(user);
        
        set({
          authTokens,
          user,
          isAuthenticated: !!authTokens.accessToken,
          error: null,
        });
      },

      setLoading: (isLoading: boolean) => set({ isLoading }),

      setError: (error: string | null) =>
        set({
          error,
          isLoading: false,
        }),

      clearError: () => set({ error: null }),

      logout: () => {
        // Clear tokens first
        TokenManager.clearAuthSession();
        
        // Reset initialization flag to prevent stale state
        isInitialized = false;
        
        // Clear all auth state
        set({
          user: null,
          authTokens: null,
          isAuthenticated: false,
          error: null,
          isLoading: false,
        });
        
        console.log('🚪 Auth store logout completed');
      },

      reset: () => {
        isInitialized = false; // Allow re-initialization after reset
        set(initialState);
      },

      initialize: (authTokens: AuthTokens | null, user: User | null) => {
        set({
          authTokens,
          user,
          isAuthenticated: !!authTokens?.accessToken,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'auth-store',
    }
  )
);

// Track if store has been initialized to prevent duplicate initialization
let isInitialized = false;

// Initialize store on app load
export const initializeAuthStore = async () => {
  // Prevent multiple initializations
  if (isInitialized) {
    console.log('🔄 Auth store already initialized, skipping...');
    return;
  }
  
  console.log('🚀 Initializing auth store...');
  
  try {
    const hasTokens = TokenManager.isAuthenticated();
    console.log('🔍 Has tokens in localStorage:', hasTokens);
    
    const tokens = hasTokens 
      ? {
          accessToken: TokenManager.getAccessToken()!,
          refreshToken: TokenManager.getRefreshToken()!,
          idToken: TokenManager.getIdToken(),
          tokenType: 'Bearer',
          expiresIn: 3600, // Default value, should be updated from actual token
        } as AuthTokens
      : null;
    
    let user = TokenManager.getUserData();
    console.log('👤 User data from localStorage:', user);
    
    // If we have tokens but no user data, we'll let the auth verification hook handle it
    // This prevents the circular dependency and infinite loops
    
    useAuthStore.getState().initialize(tokens, user);
    isInitialized = true;
    
    console.log('✅ Auth store initialized successfully', { 
      hasTokens: !!tokens, 
      hasUser: !!user,
      isAuthenticated: !!tokens?.accessToken 
    });
  } catch (error) {
    console.error('❌ Auth store initialization failed:', error);
    isInitialized = true; // Prevent retries
  }
};
